import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { usePWAInstall } from '../context/PWAInstallContext';
import PostMioLogo from '../components/PostMioLogo';
import confetti from 'canvas-confetti';
import { fixHangingConjunctions } from '../lib/typography';

const UI_TEXT = {
  IT: {
    about: 'Chi siamo',
    welcome: 'Bentornato',
    namePrompt: 'Come ti chiami?',
    namePlaceholder: 'Il tuo nome',
    nameBtn: 'Continua',
    postTitle: 'Crea i tuoi post',
    postPlaceholder: 'Descrivi il piatto del giorno, un\'offerta speciale, un evento...',
    generateBtn: 'Genera Post',
    generating: 'Generazione in corso...',
    reviewTitle: 'Rispondi a una recensione',
    reviewPlaceholder: 'Incolla qui la recensione Google...',
    reviewBtn: 'Genera Risposta',
    reviewGenerating: 'Creazione risposta...',
    copyBtn: 'Copia',
    copied: 'Copiato ✓',
    toneLabel: 'Tono',
    tones: { professional: '💼 Professionale', friendly: '😊 Amichevole', fun: '🎉 Divertente' },
    businessTypeLabel: 'Attività',
    businessTypes: {
      gastronomy:  '🍕 Ristorazione',
      beauty:      '💇 Beauty & Wellness',
      design:      '🎨 Grafica & Creative',
      coaching:    '🎤 Coaching & Consulting',
      hospitality: '🏨 Ospitalità & Turismo',
      retail:      '🛍️ Retail & Shop',
    },
    logout: 'Esci',
    postsLabel: 'I tuoi post',
    reviewLabel: 'Risposta alla recensione',
    instagram: 'Instagram',
    facebook: 'Facebook',
    whatsapp: 'WhatsApp',
    cancelBtn: 'Annulla abbonamento',
    cancelTitle: 'Annullare l\'abbonamento?',
    cancelBody: 'Puoi continuare a usare PostMio™ fino alla fine del periodo di fatturazione corrente. Non ti verrà addebitato nulla dopo.',
    cancelConfirm: 'Sì, annulla',
    cancelAbort: 'Torna indietro',
    cancelSuccess: 'Abbonamento annullato. Hai accesso fino alla fine del periodo di fatturazione.',
    cancelPending: 'Abbonamento in scadenza a fine periodo.',
    cancelSuccessTitle: 'Abbonamento annullato',
    cancelSuccessClose: 'Chiudi',
    accountLabel: 'Account',
    reviewsNav: 'Recensioni',
    reviewSectionLabel: 'LA TUA RECENSIONE',
    leaveReviewTitle: 'Lascia una recensione',
    leaveReviewSubtitle: 'Condividi la tua esperienza con PostMio™.',
    reviewTextPlaceholder: 'Scrivi qui la tua esperienza...',
    submitReviewBtn: 'Invia recensione',
    reviewSuccessMsg: 'Grazie! La tua recensione è stata inviata.',
    reviewValidationError: 'Seleziona una valutazione e scrivi la tua esperienza.',
  },
  EN: {
    about: 'About',
    welcome: 'Welcome back',
    namePrompt: "What's your name?",
    namePlaceholder: 'Your name',
    nameBtn: 'Continue',
    postTitle: 'Create your posts',
    postPlaceholder: 'Describe today\'s special, an offer, or an event...',
    generateBtn: 'Generate Posts',
    generating: 'Generating...',
    reviewTitle: 'Reply to a review',
    reviewPlaceholder: 'Paste the Google review here...',
    reviewBtn: 'Generate Reply',
    reviewGenerating: 'Creating reply...',
    copyBtn: 'Copy',
    copied: 'Copied ✓',
    toneLabel: 'Tone',
    tones: { professional: '💼 Professional', friendly: '😊 Warm & Friendly', fun: '🎉 Fun' },
    businessTypeLabel: 'Business',
    businessTypes: {
      gastronomy:  '🍕 Gastronomy',
      beauty:      '💇 Beauty & Wellness',
      design:      '🎨 Graphic Design & Creative',
      coaching:    '🎤 Coaching & Consulting',
      hospitality: '🏨 Hospitality & Tourism',
      retail:      '🛍️ Retail & Shop',
    },
    logout: 'Logout',
    postsLabel: 'Your posts',
    reviewLabel: 'Review reply',
    instagram: 'Instagram',
    facebook: 'Facebook',
    whatsapp: 'WhatsApp',
    cancelBtn: 'Cancel subscription',
    cancelTitle: 'Cancel subscription?',
    cancelBody: 'You\'ll keep full access until the end of your current billing period. No further charges after that.',
    cancelConfirm: 'Yes, cancel',
    cancelAbort: 'Go back',
    cancelSuccess: 'Subscription cancelled. You have access until the end of your billing period.',
    cancelPending: 'Subscription set to expire at end of period.',
    cancelSuccessTitle: 'Subscription cancelled',
    cancelSuccessClose: 'Close',
    accountLabel: 'Account',
    reviewsNav: 'Reviews',
    reviewSectionLabel: 'YOUR REVIEW',
    leaveReviewTitle: 'Leave a review',
    leaveReviewSubtitle: 'Share your experience with PostMio™.',
    reviewTextPlaceholder: 'Write your experience here...',
    submitReviewBtn: 'Submit review',
    reviewSuccessMsg: 'Thank you! Your review has been submitted.',
    reviewValidationError: 'Please select a rating and write your experience.',
  },
  PL: {
    about: 'O nas',
    welcome: 'Witaj ponownie',
    namePrompt: 'Jak masz na imię?',
    namePlaceholder: 'Twoje imię',
    nameBtn: 'Dalej',
    postTitle: 'Stwórz swoje posty',
    postPlaceholder: 'Opisz danie dnia, ofertę specjalną lub wydarzenie...',
    generateBtn: 'Stwórz Posty',
    generating: 'Tworzenie...',
    reviewTitle: 'Odpowiedz na recenzję',
    reviewPlaceholder: 'Wklej tutaj recenzję Google...',
    reviewBtn: 'Stwórz Odpowiedź',
    reviewGenerating: 'Tworzenie odpowiedzi...',
    copyBtn: 'Kopiuj',
    copied: 'Skopiowano ✓',
    toneLabel: 'Ton',
    tones: { professional: '💼 Profesjonalny', friendly: '😊 Przyjazny', fun: '🎉 Zabawny' },
    businessTypeLabel: 'Działalność',
    businessTypes: {
      gastronomy:  '🍕 Gastronomia',
      beauty:      '💇 Beauty & Wellness',
      design:      '🎨 Grafika & Creative',
      coaching:    '🎤 Coaching & Consulting',
      hospitality: '🏨 Gościnność & Turystyka',
      retail:      '🛍️ Handel & Sklep',
    },
    logout: 'Wyloguj',
    postsLabel: 'Twoje posty',
    reviewLabel: 'Odpowiedź na recenzję',
    instagram: 'Instagram',
    facebook: 'Facebook',
    whatsapp: 'WhatsApp',
    cancelBtn: 'Anuluj subskrypcję',
    cancelTitle: 'Anulować subskrypcję?',
    cancelBody: 'Zachowasz pełny dostęp do końca bieżącego okresu rozliczeniowego. Żadnych dalszych opłat po tym czasie.',
    cancelConfirm: 'Tak, anuluj',
    cancelAbort: 'Wróć',
    cancelSuccess: 'Subskrypcja anulowana. Masz dostęp do końca okresu rozliczeniowego.',
    cancelPending: 'Subskrypcja wygaśnie na koniec okresu.',
    cancelSuccessTitle: 'Subskrypcja anulowana',
    cancelSuccessClose: 'Zamknij',
    accountLabel: 'Konto',
    reviewsNav: 'Opinie',
    reviewSectionLabel: 'TWOJA OPINIA',
    leaveReviewTitle: 'Zostaw opinię',
    leaveReviewSubtitle: 'Podziel się swoją opinią o PostMio™.',
    reviewTextPlaceholder: 'Napisz tutaj swoją opinię...',
    submitReviewBtn: 'Wyślij opinię',
    reviewSuccessMsg: 'Dziękujemy! Twoja opinia została przesłana.',
    reviewValidationError: 'Wybierz ocenę i napisz swoją opinię.',
  },
  ES: {
    about: 'Acerca de',
    welcome: 'Bienvenido de nuevo',
    namePrompt: '¿Cómo te llamas?',
    namePlaceholder: 'Tu nombre',
    nameBtn: 'Continuar',
    postTitle: 'Crea tus publicaciones',
    postPlaceholder: 'Describe el plato del día, una oferta especial, un evento...',
    generateBtn: 'Generar Publicaciones',
    generating: 'Generando...',
    reviewTitle: 'Responder a una reseña',
    reviewPlaceholder: 'Pega aquí la reseña de Google...',
    reviewBtn: 'Generar Respuesta',
    reviewGenerating: 'Creando respuesta...',
    copyBtn: 'Copiar',
    copied: 'Copiado ✓',
    toneLabel: 'Tono',
    tones: { professional: '💼 Profesional', friendly: '😊 Amigable', fun: '🎉 Divertido' },
    businessTypeLabel: 'Negocio',
    businessTypes: {
      gastronomy:  '🍕 Gastronomía',
      beauty:      '💇 Beauty & Wellness',
      design:      '🎨 Diseño Gráfico & Creativo',
      coaching:    '🎤 Coaching & Consultoría',
      hospitality: '🏨 Hospitalidad & Turismo',
      retail:      '🛍️ Comercio & Tienda',
    },
    logout: 'Cerrar sesión',
    postsLabel: 'Tus publicaciones',
    reviewLabel: 'Respuesta a la reseña',
    instagram: 'Instagram',
    facebook: 'Facebook',
    whatsapp: 'WhatsApp',
    cancelBtn: 'Cancelar suscripción',
    cancelTitle: '¿Cancelar suscripción?',
    cancelBody: 'Conservarás el acceso completo hasta el final de tu período de facturación actual. Sin más cargos después de eso.',
    cancelConfirm: 'Sí, cancelar',
    cancelAbort: 'Volver',
    cancelSuccess: 'Suscripción cancelada. Tienes acceso hasta el final de tu período de facturación.',
    cancelPending: 'La suscripción vencerá al final del período.',
    cancelSuccessTitle: 'Suscripción cancelada',
    cancelSuccessClose: 'Cerrar',
    accountLabel: 'Cuenta',
    reviewsNav: 'Reseñas',
    reviewSectionLabel: 'TU RESEÑA',
    leaveReviewTitle: 'Dejar una reseña',
    leaveReviewSubtitle: 'Comparte tu experiencia con PostMio™.',
    reviewTextPlaceholder: 'Escribe tu experiencia aquí...',
    submitReviewBtn: 'Enviar reseña',
    reviewSuccessMsg: '¡Gracias! Tu reseña ha sido enviada.',
    reviewValidationError: 'Por favor, selecciona una valoración y escribe tu experiencia.',
  },
  DE: {
    about: 'Über uns',
    welcome: 'Willkommen zurück',
    namePrompt: 'Wie heißen Sie?',
    namePlaceholder: 'Ihr Name',
    nameBtn: 'Weiter',
    postTitle: 'Beiträge erstellen',
    postPlaceholder: 'Beschreiben Sie das Tagesgericht, ein Angebot oder ein Event...',
    generateBtn: 'Beiträge generieren',
    generating: 'Wird generiert...',
    reviewTitle: 'Auf eine Bewertung antworten',
    reviewPlaceholder: 'Google-Bewertung hier einfügen...',
    reviewBtn: 'Antwort generieren',
    reviewGenerating: 'Antwort wird erstellt...',
    copyBtn: 'Kopieren',
    copied: 'Kopiert ✓',
    toneLabel: 'Ton',
    tones: { professional: '💼 Professionell', friendly: '😊 Freundlich', fun: '🎉 Lustig' },
    businessTypeLabel: 'Branche',
    businessTypes: {
      gastronomy:  '🍕 Gastronomie',
      beauty:      '💇 Beauty & Wellness',
      design:      '🎨 Grafikdesign & Kreativ',
      coaching:    '🎤 Coaching & Beratung',
      hospitality: '🏨 Gastgewerbe & Tourismus',
      retail:      '🛍️ Handel & Shop',
    },
    logout: 'Abmelden',
    postsLabel: 'Ihre Beiträge',
    reviewLabel: 'Bewertungsantwort',
    instagram: 'Instagram',
    facebook: 'Facebook',
    whatsapp: 'WhatsApp',
    cancelBtn: 'Abonnement kündigen',
    cancelTitle: 'Abonnement kündigen?',
    cancelBody: 'Sie behalten den vollen Zugang bis zum Ende Ihres aktuellen Abrechnungszeitraums. Danach keine weiteren Kosten.',
    cancelConfirm: 'Ja, kündigen',
    cancelAbort: 'Zurück',
    cancelSuccess: 'Abonnement gekündigt. Sie haben Zugang bis zum Ende Ihres Abrechnungszeitraums.',
    cancelPending: 'Abonnement läuft am Ende des Zeitraums ab.',
    cancelSuccessTitle: 'Abonnement gekündigt',
    cancelSuccessClose: 'Schließen',
    accountLabel: 'Konto',
    reviewsNav: 'Bewertungen',
    reviewSectionLabel: 'IHRE BEWERTUNG',
    leaveReviewTitle: 'Bewertung hinterlassen',
    leaveReviewSubtitle: 'Teilen Sie Ihre Erfahrung mit PostMio™.',
    reviewTextPlaceholder: 'Schreiben Sie Ihre Erfahrung hier...',
    submitReviewBtn: 'Bewertung senden',
    reviewSuccessMsg: 'Danke! Ihre Bewertung wurde eingereicht.',
    reviewValidationError: 'Bitte wählen Sie eine Bewertung und schreiben Sie Ihre Erfahrung.',
  },
  FR: {
    about: 'À propos',
    welcome: 'Bon retour',
    namePrompt: 'Comment vous appelez-vous ?',
    namePlaceholder: 'Votre nom',
    nameBtn: 'Continuer',
    postTitle: 'Créez vos publications',
    postPlaceholder: 'Décrivez le plat du jour, une offre spéciale, un événement...',
    generateBtn: 'Générer des publications',
    generating: 'Génération en cours...',
    reviewTitle: 'Répondre à un avis',
    reviewPlaceholder: 'Collez l\'avis Google ici...',
    reviewBtn: 'Générer une réponse',
    reviewGenerating: 'Création de la réponse...',
    copyBtn: 'Copier',
    copied: 'Copié ✓',
    toneLabel: 'Ton',
    tones: { professional: '💼 Professionnel', friendly: '😊 Amical', fun: '🎉 Fun' },
    businessTypeLabel: 'Activité',
    businessTypes: {
      gastronomy:  '🍕 Gastronomie',
      beauty:      '💇 Beauty & Wellness',
      design:      '🎨 Design Graphique & Créatif',
      coaching:    '🎤 Coaching & Conseil',
      hospitality: '🏨 Hôtellerie & Tourisme',
      retail:      '🛍️ Commerce & Boutique',
    },
    logout: 'Déconnexion',
    postsLabel: 'Vos publications',
    reviewLabel: 'Réponse à l\'avis',
    instagram: 'Instagram',
    facebook: 'Facebook',
    whatsapp: 'WhatsApp',
    cancelBtn: 'Résilier l\'abonnement',
    cancelTitle: 'Résilier l\'abonnement ?',
    cancelBody: 'Vous conserverez un accès complet jusqu\'à la fin de votre période de facturation en cours. Aucun frais supplémentaire après cela.',
    cancelConfirm: 'Oui, résilier',
    cancelAbort: 'Retour',
    cancelSuccess: 'Abonnement résilié. Vous avez accès jusqu\'à la fin de votre période de facturation.',
    cancelPending: 'L\'abonnement expirera à la fin de la période.',
    cancelSuccessTitle: 'Abonnement résilié',
    cancelSuccessClose: 'Fermer',
    accountLabel: 'Compte',
    reviewsNav: 'Avis',
    reviewSectionLabel: 'VOTRE AVIS',
    leaveReviewTitle: 'Laisser un avis',
    leaveReviewSubtitle: 'Partagez votre expérience avec PostMio™.',
    reviewTextPlaceholder: 'Écrivez votre expérience ici...',
    submitReviewBtn: 'Soumettre l\'avis',
    reviewSuccessMsg: 'Merci ! Votre avis a été soumis.',
    reviewValidationError: 'Veuillez sélectionner une note et écrire votre expérience.',
  },
};

function detectLang() {
  const lang = (navigator.language || '').toLowerCase()
  if (lang.startsWith('pl')) return 'PL'
  if (lang.startsWith('en')) return 'EN'
  if (lang.startsWith('es')) return 'ES'
  if (lang.startsWith('de')) return 'DE'
  if (lang.startsWith('fr')) return 'FR'
  return 'IT'
}

function renderTM(s) {
  if (typeof s !== 'string' || !s.includes('™')) return s
  const i = s.indexOf('™')
  return <>{s.slice(0, i)}<sup style={{fontSize:'0.5em',verticalAlign:'super',lineHeight:0}}>™</sup>{s.slice(i + 1)}</>
}

const PLATFORM_ICONS = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
    </svg>
  )
};

function CopyButton({ text, label, copied: copiedLabel }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <button
      onClick={handleCopy}
      className={`text-xs font-semibold px-4 py-2 rounded-lg transition-all tracking-wider ${
        copied
          ? 'bg-[#C9922A] text-[#0A0A0A]'
          : 'bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#C9922A]/20 hover:text-[#C9922A]'
      }`}
    >
      {copied ? copiedLabel : label}
    </button>
  );
}

function PostCard({ platform, icon, content, displayContent, t }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#2C2C2C] rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#C9922A]">
          {icon}
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A0A0A0]">{platform}</span>
        </div>
        <CopyButton text={content} label={t.copyBtn} copied={t.copied} />
      </div>
      <p className="text-[#F0EDE8] text-sm leading-snug sm:leading-relaxed whitespace-pre-wrap">{displayContent}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, profile: authProfile, signOut } = useAuth();
  const { deferredPrompt, setDeferredPrompt } = usePWAInstall();
  const [profile, setProfile] = useState(authProfile);
  const [loading, setLoading] = useState(!authProfile);

  // Name capture state
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Business type state (synced to profile)
  const [businessType, setBusinessType] = useState(authProfile?.business_type || 'gastronomy');

  // Post generation state
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const [posts, setPosts] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Review state
  const [reviewPrompt, setReviewPrompt] = useState('');
  const [reviewResult, setReviewResult] = useState('');
  const [isGeneratingReview, setIsGeneratingReview] = useState(false);

  // Cancel subscription state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);

  // User review state
  const [reviewRating, setReviewRating] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewValidationError, setReviewValidationError] = useState('');
  const [hasExistingReview, setHasExistingReview] = useState(false);

  useEffect(() => {
    if (authProfile) {
      setProfile(authProfile);
      setBusinessType(authProfile.business_type || 'gastronomy');
      setLoading(false);
    } else {
      fetchProfile();
    }
  }, [authProfile]);

  useEffect(() => {
    if (!profile?.language || !profile?.name) return;
    const KEY = 'postmio_confetti_fired';
    if (sessionStorage.getItem(KEY)) return;
    sessionStorage.setItem(KEY, '1');
    const end = Date.now() + 1500;
    const colors = ['#C9922A', '#E5B050', '#F0EDE8', '#ffffff', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'];
    (function frame() {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [profile]);

  async function fetchProfile() {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { data } = await supabase.from('profiles').select('*').eq('id', currentUser.id).maybeSingle();
        setProfile(data);
        setBusinessType(data?.business_type || 'gastronomy');
      }
    } finally {
      setLoading(false);
    }
  }

  async function saveName() {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    setSavingName(true);
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    await supabase.from('profiles').update({ name: trimmed }).eq('id', currentUser.id);
    await fetchProfile();
    setSavingName(false);
  }

  async function setLanguage(lang) {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    await supabase.from('profiles').update({ language: lang }).eq('id', currentUser.id);
    fetchProfile();
  }

  async function saveBusinessType(value) {
    setBusinessType(value);
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    await supabase.from('profiles').update({ business_type: value }).eq('id', currentUser.id);
  }

  async function getToken() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  }

  async function handleGenerate() {
    if (!prompt.trim()) return;
    setIsGenerating(true);
    setPosts(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ prompt, mode: 'posts', tone, business_type: businessType })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setPosts({ instagram: data.instagram, facebook: data.facebook, whatsapp: data.whatsapp });
    } catch (error) {
      alert('Errore: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleGenerateReview() {
    if (!reviewPrompt.trim()) return;
    setIsGeneratingReview(true);
    setReviewResult('');
    try {
      const token = await getToken();
      const response = await fetch('/api/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ prompt: reviewPrompt, mode: 'review', business_type: businessType })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setReviewResult(data.review);
    } catch (error) {
      alert('Errore: ' + error.message);
    } finally {
      setIsGeneratingReview(false);
    }
  }

  async function handleCancel() {
    setIsCanceling(true);
    try {
      const token = await getToken();
      const response = await fetch('/api/cancel', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Error');
      setCancelDone(true);
      setShowCancelDialog(false);
      setShowCancelSuccess(true);
    } catch (error) {
      alert(error.message);
    } finally {
      setIsCanceling(false);
    }
  }

  // Fetch existing review on mount (after user is known)
  useEffect(() => {
    if (!user) return;
    async function fetchExistingReview() {
      const { data } = await supabase
        .from('reviews')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) {
        setHasExistingReview(true);
      }
    }
    fetchExistingReview();
  }, [user]);

  async function handleSubmitReview() {
    if (!reviewRating || !reviewText.trim()) {
      setReviewValidationError(tb.reviewValidationError);
      return;
    }
    setReviewValidationError('');
    setIsSubmittingReview(true);
    try {
      const token = await getToken();
      const response = await fetch('/api/submit-review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: reviewRating, review_text: reviewText }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error');
      setReviewSubmitted(true);
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setIsSubmittingReview(false);
    }
  }

  // Loading spinner
  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-[#0A0A0A]">
      <div className="w-10 h-10 border-2 border-[#2C2C2C] border-t-[#C9922A] rounded-full animate-spin" />
    </div>
  );

  const t = UI_TEXT[profile?.language || 'IT'];
  const tb = UI_TEXT[detectLang()];

  // Language selection screen for new users
  if (profile && !profile.language) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#0A0A0A] px-6">
        <div className="mb-12 text-center">
          <Link to="/" className="inline-block">
            <PostMioLogo size="lg" variant="dark" />
          </Link>
          <p className="text-[#7A7A7A] mt-3 text-sm tracking-widest uppercase">Scegli la tua lingua</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full max-w-lg">
          {[
            { code: 'IT', flag: '🇮🇹', label: 'Italiano' },
            { code: 'EN', flag: '🇬🇧', label: 'English' },
            { code: 'PL', flag: '🇵🇱', label: 'Polski' },
            { code: 'ES', flag: '🇪🇸', label: 'Español' },
            { code: 'DE', flag: '🇩🇪', label: 'Deutsch' },
            { code: 'FR', flag: '🇫🇷', label: 'Français' },
          ].map(({ code, flag, label }) => (
            <button
              key={code}
              onClick={() => setLanguage(code)}
              className="flex flex-col items-center gap-3 p-8 bg-[#141414] border border-[#2C2C2C] rounded-2xl hover:border-[#C9922A] transition-all group"
            >
              <span className="text-4xl">{flag}</span>
              <span className="text-[#A0A0A0] group-hover:text-[#C9922A] text-sm font-semibold tracking-widest uppercase transition-colors">{label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Name capture screen — shown once for any user with no name set
  if (profile && profile.language && !profile.name) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#0A0A0A] px-6">
        <div className="mb-12 text-center">
          <Link to="/" className="inline-block">
            <PostMioLogo size="lg" variant="dark" />
          </Link>
        </div>
        <div className="w-full max-w-sm bg-[#141414] border border-[#2C2C2C] rounded-2xl p-8 space-y-6">
          <h2 className="text-2xl font-bold text-[#F0EDE8] text-center" style={{ fontFamily: 'Lora, serif' }}>
            {t.namePrompt}
          </h2>
          <input
            type="text"
            autoFocus
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && saveName()}
            placeholder={t.namePlaceholder}
            className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl px-5 py-4 text-[#F0EDE8] text-base focus:border-[#C9922A] outline-none transition-all placeholder-[#606060]"
          />
          <button
            onClick={saveName}
            disabled={savingName || !nameInput.trim()}
            className="w-full bg-[#C9922A] hover:bg-[#E5B050] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] font-bold py-4 rounded-xl text-sm uppercase tracking-[0.2em] transition-all"
          >
            {savingName ? (
              <span className="flex items-center justify-center gap-3">
                <span className="w-4 h-4 border-2 border-[#0A0A0A]/30 border-t-[#0A0A0A] rounded-full animate-spin inline-block" />
              </span>
            ) : t.nameBtn}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0EDE8]" style={{ fontFamily: 'DM Sans, sans-serif' }}>

      {/* Navbar */}
      <nav className="border-b border-[#2C2C2C] bg-[#0A0A0A] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between dash-nav-inner">
          <Link to="/" className="inline-flex items-center">
            <PostMioLogo size="md" variant="dark" />
          </Link>
          <div className="flex items-center gap-6 dash-nav-links">
            <Link to="/about" className="text-xs uppercase tracking-[0.2em] text-[#C9922A] hover:text-[#E5B050] transition-colors">
              {tb.about}
            </Link>
            <Link to="/reviews" className="text-xs uppercase tracking-[0.2em] text-[#C9922A] hover:text-[#E5B050] transition-colors">
              {tb.reviewsNav}
            </Link>
            <button
              onClick={() => setLanguage(null)}
              className="text-xs uppercase tracking-[0.2em] text-[#C9922A] hover:text-[#E5B050] transition-colors"
            >
              {profile?.language}
            </button>
            <button
              onClick={signOut}
              style={{
                background: 'transparent',
                border: '1.5px solid #A02020',
                borderRadius: '10px',
                padding: '7px 16px',
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#A02020',
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#FF4D4D'; e.currentTarget.style.borderColor = '#FF4D4D' }}
              onMouseLeave={e => { e.currentTarget.style.color = '#A02020'; e.currentTarget.style.borderColor = '#A02020' }}
            >
              {tb.logout}
            </button>
          </div>
        </div>
      </nav>

      {/* PWA Install Banner */}
      {deferredPrompt && (
        <div style={{ background: '#141414', borderBottom: '1px solid #2C2C2C', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap', textAlign: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#F0EDE8', fontFamily: "'DM Sans', sans-serif" }}>
            Download the PostMio™ App now.
          </span>
          <button
            className="bg-[#C9922A] hover:bg-[#E5B050] text-[#0A0A0A] font-bold py-2 px-5 rounded-xl text-xs uppercase tracking-[0.2em] transition-all"
            onClick={async () => { deferredPrompt.prompt(); await deferredPrompt.userChoice; }}
          >
            Install App
          </button>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-12">

        {/* Header */}
        <header>
          <p className="text-[#7A7A7A] text-sm uppercase tracking-[0.2em] mb-1">{t.welcome}</p>
          <h2 className="text-3xl font-bold text-[#F0EDE8]" style={{ fontFamily: 'Lora, serif' }}>
            {profile?.name || profile?.email?.split('@')[0]}
          </h2>
          <div className="w-10 h-0.5 bg-[#C9922A] mt-3 rounded-full" />
        </header>

        {/* POST GENERATOR */}
        <section className="bg-[#141414] border border-[#2C2C2C] rounded-2xl p-8 space-y-6">
          <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#C9922A]">{tb.postTitle}</h3>

          <textarea
            className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl p-5 text-[#F0EDE8] text-base focus:border-[#C9922A] outline-none transition-all resize-none h-36 placeholder-[#606060]"
            placeholder={tb.postPlaceholder}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
          />

          {/* Tone + Business type selectors */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">

            {/* Tone selector */}
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A] shrink-0" htmlFor="tone-select">
                {tb.toneLabel}:
              </label>
              <div className="relative">
                <select
                  id="tone-select"
                  value={tone}
                  onChange={e => setTone(e.target.value)}
                  className="appearance-none bg-[#0A0A0A] border border-[#2C2C2C] hover:border-[#C9922A]/50 focus:border-[#C9922A] text-[#F0EDE8] text-sm rounded-xl pl-4 pr-10 py-2.5 outline-none transition-all cursor-pointer w-[180px] max-w-full"
                >
                  {Object.entries(tb.tones).map(([key, label]) => (
                    <option key={key} value={key} className="bg-[#141414] text-[#F0EDE8]">
                      {label}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </div>

            {/* Business type selector */}
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A] shrink-0" htmlFor="business-type-select">
                {tb.businessTypeLabel}:
              </label>
              <div className="relative">
                <select
                  id="business-type-select"
                  value={businessType}
                  onChange={e => saveBusinessType(e.target.value)}
                  className="appearance-none bg-[#0A0A0A] border border-[#2C2C2C] hover:border-[#C9922A]/50 focus:border-[#C9922A] text-[#F0EDE8] text-sm rounded-xl pl-4 pr-10 py-2.5 outline-none transition-all cursor-pointer w-[180px] max-w-full"
                >
                  {Object.entries(tb.businessTypes).map(([key, label]) => (
                    <option key={key} value={key} className="bg-[#141414] text-[#F0EDE8]">
                      {label}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </div>

          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            className="w-full bg-[#C9922A] hover:bg-[#E5B050] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] font-bold py-4 rounded-xl text-sm uppercase tracking-[0.2em] transition-all"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-3">
                <span className="w-4 h-4 border-2 border-[#0A0A0A]/30 border-t-[#0A0A0A] rounded-full animate-spin inline-block" />
                {tb.generating}
              </span>
            ) : tb.generateBtn}
          </button>
        </section>

        {/* RESULTS — 3 POSTS */}
        {posts && (
          <section className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-[#7A7A7A]">{tb.postsLabel}</h3>
            <PostCard platform={tb.instagram} icon={PLATFORM_ICONS.instagram} content={posts.instagram} displayContent={fixHangingConjunctions(posts.instagram, profile?.language)} t={tb} />
            <PostCard platform={tb.facebook} icon={PLATFORM_ICONS.facebook} content={posts.facebook} displayContent={fixHangingConjunctions(posts.facebook, profile?.language)} t={tb} />
            <PostCard platform={tb.whatsapp} icon={PLATFORM_ICONS.whatsapp} content={posts.whatsapp} displayContent={fixHangingConjunctions(posts.whatsapp, profile?.language)} t={tb} />
          </section>
        )}

        {/* REVIEW RESPONDER */}
        <section className="bg-[#141414] border border-[#2C2C2C] rounded-2xl p-8 space-y-6">
          <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#C9922A]">{tb.reviewTitle}</h3>

          <textarea
            className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl p-5 text-[#F0EDE8] text-base focus:border-[#C9922A] outline-none transition-all resize-none h-36 placeholder-[#606060]"
            placeholder={tb.reviewPlaceholder}
            value={reviewPrompt}
            onChange={e => setReviewPrompt(e.target.value)}
          />

          <button
            onClick={handleGenerateReview}
            disabled={isGeneratingReview || !reviewPrompt.trim()}
            className="w-full bg-[#2C2C2C] hover:bg-[#C9922A]/20 hover:border-[#C9922A] border border-[#2C2C2C] disabled:opacity-40 disabled:cursor-not-allowed text-[#F0EDE8] font-bold py-4 rounded-xl text-sm uppercase tracking-[0.2em] transition-all"
          >
            {isGeneratingReview ? (
              <span className="flex items-center justify-center gap-3">
                <span className="w-4 h-4 border-2 border-[#F0EDE8]/30 border-t-[#F0EDE8] rounded-full animate-spin inline-block" />
                {tb.reviewGenerating}
              </span>
            ) : tb.reviewBtn}
          </button>

          {reviewResult && (
            <div className="bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#7A7A7A]">{tb.reviewLabel}</span>
                <CopyButton text={reviewResult} label={tb.copyBtn} copied={tb.copied} />
              </div>
              <p className="text-[#F0EDE8] text-sm leading-relaxed whitespace-pre-wrap">{fixHangingConjunctions(reviewResult, profile?.language)}</p>
            </div>
          )}
        </section>

        {/* LEAVE A REVIEW */}
        <div style={{ maxWidth: 480 }}>
          <div
            style={{
              background: '#0A0A0A',
              border: '1px solid #2C2C2C',
              borderRadius: 8,
              padding: 20,
            }}
          >
            <p
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#7A7A7A',
                marginBottom: 6,
              }}
            >
              {tb.reviewSectionLabel}
            </p>
            <h3
              style={{
                fontFamily: 'Lora, serif',
                fontSize: 16,
                fontWeight: 700,
                color: '#F0EDE8',
                marginBottom: 4,
              }}
            >
              {tb.leaveReviewTitle}
            </h3>
            <p
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                color: '#7A7A7A',
                marginBottom: 16,
              }}
            >
              {renderTM(tb.leaveReviewSubtitle)}
            </p>

            {(hasExistingReview || reviewSubmitted) ? (
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: '#C9922A', fontWeight: 600 }}>
                {tb.reviewSuccessMsg}
              </p>
            ) : (
              <>
                {/* Star rating */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => { setReviewRating(star); setReviewValidationError(''); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 22,
                        color: reviewRating >= star ? '#C9922A' : '#7A7A7A',
                        padding: 0,
                        lineHeight: 1,
                        transition: 'transform 0.1s',
                      }}
                      aria-label={`${star} star`}
                    >
                      ★
                    </button>
                  ))}
                </div>

                {/* Textarea + character counter */}
                <div style={{ marginBottom: 8 }}>
                  <textarea
                    rows={3}
                    maxLength={600}
                    placeholder={tb.reviewTextPlaceholder}
                    value={reviewText}
                    onChange={e => { setReviewText(e.target.value); setReviewValidationError(''); }}
                    style={{
                      width: '100%',
                      background: '#141414',
                      border: '1px solid #2C2C2C',
                      borderRadius: 6,
                      padding: '10px 12px',
                      color: '#F0EDE8',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 14,
                      resize: 'none',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#C9922A'; }}
                    onBlur={e => { e.target.style.borderColor = '#2C2C2C'; }}
                  />
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: '#7A7A7A', textAlign: 'right', marginTop: 2 }}>
                    {reviewText.length} / 600
                  </p>
                </div>

                {/* Validation error */}
                {reviewValidationError && (
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: '#A02020', marginBottom: 8 }}>
                    {reviewValidationError}
                  </p>
                )}

                {/* Submit button */}
                <button
                  onClick={handleSubmitReview}
                  disabled={isSubmittingReview}
                  style={{
                    background: '#C9922A',
                    color: '#0A0A0A',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 20px',
                    fontSize: 14,
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 700,
                    cursor: isSubmittingReview ? 'not-allowed' : 'pointer',
                    opacity: isSubmittingReview ? 0.4 : 1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    transition: 'background 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  onMouseEnter={e => { if (!isSubmittingReview) e.currentTarget.style.background = '#E5B050'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#C9922A'; }}
                >
                  {isSubmittingReview ? (
                    <span
                      style={{
                        width: 14, height: 14,
                        border: '2px solid rgba(10,10,10,0.3)',
                        borderTop: '2px solid #0A0A0A',
                        borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'spin 0.8s linear infinite',
                      }}
                    />
                  ) : tb.submitReviewBtn}
                </button>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </>
            )}
          </div>
        </div>

        {/* ACCOUNT / SUBSCRIPTION */}
        <section className="border-t border-[#1E1E1E] pt-8">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#606060] mb-4">{tb.accountLabel}</h3>
          {(cancelDone || profile?.cancel_at_period_end || profile?.subscription_status === 'canceled') ? (
            <p className="text-xs text-[#606060]">
              {profile?.subscription_status === 'canceled' ? tb.cancelPending : tb.cancelSuccess}
            </p>
          ) : (
            <button
              onClick={() => setShowCancelDialog(true)}
              className="text-xs text-[#606060] hover:text-[#A0A0A0] transition-colors underline underline-offset-4"
            >
              {tb.cancelBtn}
            </button>
          )}
        </section>

      </main>

      {/* CANCEL CONFIRMATION DIALOG */}
      {showCancelDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
          onClick={() => !isCanceling && setShowCancelDialog(false)}
        >
          <div
            className="bg-[#141414] border border-[#2C2C2C] rounded-2xl p-8 w-full max-w-sm space-y-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="space-y-3">
              <h3 className="text-base font-bold text-[#F0EDE8]" style={{ fontFamily: 'Lora, serif' }}>
                {tb.cancelTitle}
              </h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">
                {renderTM(tb.cancelBody)}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleCancel}
                disabled={isCanceling}
                className="w-full bg-[#2C2C2C] hover:bg-[#3A3A3A] disabled:opacity-40 disabled:cursor-not-allowed text-[#A0A0A0] font-semibold py-3 rounded-xl text-sm transition-all"
              >
                {isCanceling ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-[#A0A0A0]/30 border-t-[#A0A0A0] rounded-full animate-spin inline-block" />
                  </span>
                ) : tb.cancelConfirm}
              </button>
              <button
                onClick={() => setShowCancelDialog(false)}
                disabled={isCanceling}
                className="w-full bg-[#C9922A] hover:bg-[#E5B050] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] font-bold py-3 rounded-xl text-sm transition-all"
              >
                {tb.cancelAbort}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL SUCCESS MODAL */}
      {showCancelSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
          onClick={() => setShowCancelSuccess(false)}
        >
          <div
            className="bg-[#141414] border border-[#C9922A] rounded-2xl p-8 w-full max-w-sm space-y-6 text-center"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center">
              <PostMioLogo size="md" variant="dark" />
            </div>
            <div className="space-y-3">
              <h3 className="text-base font-bold text-[#F0EDE8]" style={{ fontFamily: 'Lora, serif' }}>
                {tb.cancelSuccessTitle}
              </h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">
                {tb.cancelSuccess}
              </p>
            </div>
            <button
              onClick={() => setShowCancelSuccess(false)}
              className="w-full bg-[#C9922A] hover:bg-[#E5B050] text-[#0A0A0A] font-bold py-3 rounded-xl text-sm uppercase tracking-[0.2em] transition-all"
            >
              {tb.cancelSuccessClose}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
